/**
 * AISearchBar — natural-language candidate search powered by GPT-5.2.
 *
 * - Stateless wrapper component: parent owns the query string + results.
 * - Calls candidatesApi.semanticSearch on submit and lifts results up.
 * - Surfaces the "considered vs returned" stat so users understand the
 *   pre-filter behaviour.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { candidatesApi, formatApiError } from '../lib/api';
import { toast } from 'sonner';

export default function AISearchBar({
    onResults,
    onClear,
    active = false,
    className = '',
}) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q || loading) return;
        setLoading(true);
        try {
            const res = await candidatesApi.semanticSearch(q, { limit: 20 });
            onResults?.(q, res.data);
        } catch (error) {
            toast.error(formatApiError(error) || t('ai_search.ai_unavailable'));
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setQuery('');
        onClear?.();
    };

    return (
        <div
            className={`relative bg-gradient-to-r from-[#EEF2FF] via-white to-[#FDF4FF] border border-[#E0E7FF] rounded-xl p-5 ${className}`}
            data-testid="ai-search-bar"
        >
            <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#002FA7] flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('ai_search.title')}
                        </h3>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#002FA7] text-white">
                            {t('ai_search.powered_by_ai')}
                        </span>
                    </div>
                    <p className="text-sm text-[#4B5563] mt-0.5">{t('ai_search.subtitle')}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <Input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('ai_search.placeholder')}
                        maxLength={500}
                        className="pl-3 pr-9 h-10 text-sm"
                        data-testid="ai-search-input"
                        disabled={loading}
                    />
                    {query && !loading && (
                        <button
                            type="button"
                            onClick={() => setQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#9CA3AF] hover:text-[#4B5563]"
                            aria-label={t('ai_search.clear')}
                            data-testid="ai-search-clear-input"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <Button
                    type="submit"
                    disabled={!query.trim() || loading}
                    className="bg-[#002FA7] hover:bg-[#001f7a] text-white h-10 px-5"
                    data-testid="ai-search-submit"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('ai_search.submitting')}
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {t('ai_search.submit')}
                        </>
                    )}
                </Button>
                {active && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClear}
                        className="h-10"
                        data-testid="ai-search-clear-results"
                    >
                        <X className="w-4 h-4 mr-1.5" />
                        {t('ai_search.clear')}
                    </Button>
                )}
            </form>
            {!active && (
                <p className="text-xs text-[#6B7280] mt-2 italic">{t('ai_search.tip')}</p>
            )}
        </div>
    );
}
